
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import {StackedBarChart, StackBar} from 'components/Chart'
import Icon from 'components/Icon'
import maxBy from 'lodash/maxBy'
import classnames from 'classnames'
import partial from 'utils/partial'
import propsChanged from 'utils/propsChanged'

const HANDLER_WIDTH = 8

export default class RangeSlider extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    title: PropTypes.string,
    width: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    values: PropTypes.object,
    onSelect: PropTypes.func,
    dataKey: PropTypes.string,
    xAxisKey: PropTypes.string,
    step: PropTypes.number,
    chartData: PropTypes.array,
    formatValue: PropTypes.func,
    stepValues: PropTypes.array,
    limit: PropTypes.number,
    disabled: PropTypes.bool,
    tooltip: PropTypes.func,
    showEmptyPlaceholders: PropTypes.bool,
    showClearIcon: PropTypes.bool
  }

  static defaultProps = {
    xAxisKey: 'date',
    step: 1,
    width: 200,
    limit: 0
  }

  constructor(props) {
    super(props)
    this.state = {
      handling: null,
      leftPosition: null,
      rightPosition: null,
      minValue: null,
      maxValue: null,
      distance: null,
      isOpened: null
    }
  }

  componentWillMount() {
    this.calculateValues(this.props)
  }

  componentWillReceiveProps(nextProps) {
    if (propsChanged(this.props, nextProps, ['width', 'values', 'min', 'max'])) {
      this.calculateValues(nextProps)
    }
  }

  componentWillUpdate(props, state) {
    if (state.isOpened) {
      document.addEventListener('click', this.hideSlider)
    } else {
      document.removeEventListener('click', this.hideSlider)
    }
    if (state.handling) {
      document.body.addEventListener('mousemove', this.onMoveHandling)
      document.body.addEventListener('mouseup', this.onStopHandling)
    } else {
      document.body.removeEventListener('mouseup', this.onStopHandling)
      document.body.removeEventListener('mousemove', this.onMoveHandling)
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener('mouseup', this.onStopHandling)
    document.body.removeEventListener('mousemove', this.onMoveHandling)
    document.removeEventListener('click', this.hideSlider)
  }

  render() {
    const {className, chartData, width, disabled} = this.props
    const {handling, isOpened} = this.state

    if (!width) return null

    const classNames = classnames('range-slider', className, {
      'with-chart': !!chartData,
      'is-active': !!handling,
      'is-opened': isOpened,
      'is-disabled': disabled
    })

    return (
      <div className={classNames} onClick={this.onClick}>
        {this.renderTitle()}
        {this.renderValues()}
        {this.renderChart()}
        {this.renderSlider()}
      </div>
    )
  }

  renderTitle() {
    const {title, showClearIcon, values} = this.props

    if (!title) return null

    if (showClearIcon && values) {
      return (
        <div className='range-slider_title'>
          {title}
          <Icon
            className='range-slider_title_icon'
            tooltip='Clear interval'
            onClick={this.onClearClick}
            name='cross'
            size='6px'
          />
        </div>
      )
    } else {
      return (
        <div className='range-slider_title'>{title}</div>
      )
    }
  }

  renderValues() {
    const {formatValue, values, showEmptyPlaceholders, min, max} = this.props
    const {minValue, maxValue} = this.state

    let formattedMinValue = formatValue ? formatValue(minValue) : minValue
    let formattedMaxValue = formatValue ? formatValue(maxValue) : maxValue

    if (!values && showEmptyPlaceholders && minValue === min && maxValue === max) {
      formattedMinValue = '—'
      formattedMaxValue = '—'
    }

    const classNames = classnames('range-slider_values')

    return (
      <div className={classNames}>
        <div className='range-slider_values_min'>
          <div className='range-slider_value_title'>Min</div>
          <div className='range-slider_value'>{formattedMinValue}</div>
        </div>
        <div className='range-slider_values_max'>
          <div className='range-slider_value_title'>Max</div>
          <div className='range-slider_value'>{formattedMaxValue}</div>
        </div>
      </div>
    )
  }

  renderSlider() {
    const {chartData, width} = this.props
    const {leftPosition, rightPosition, isOpened} = this.state

    if (!chartData && !isOpened) return null

    const classNames = classnames('range-slider_slider_container', {
      'is-opened': isOpened
    })

    const style = {
      width: isOpened ? width : null
    }

    return (
      <div className={classNames} style={style}>
        <div className='range-slider_slider' onClick={this.onPreventClick}>
          <div className='range-slider_slider_background' />
          <div
            className='range-slider_slider_selected'
            style={{
              left: leftPosition,
              right: width - rightPosition
            }}
          />
          <div
            className='range-slider_slider_handle range-slider_slider_handle_left'
            onMouseDown={partial(this.onStartHandling, 'left')}
            style={{transform: `translate(${leftPosition}px)`}}
          />
          <div
            className='range-slider_slider_handle range-slider_slider_handle_right'
            onMouseDown={partial(this.onStartHandling, 'right')}
            style={{transform: `translate(${rightPosition}px)`}}
          />
        </div>
      </div>
    )
  }

  renderChart() {
    const {chartData, dataKey, xAxisKey, width, limit, tooltip} = this.props

    if (!chartData && !dataKey) return null

    if (!chartData || chartData.length === 0) {
      return (
        <div className='range-slider_chart' />
      )
    }

    const {minValue, maxValue} = this.state
    const processedData = chartData.map((item, index) => {
      const value = item[xAxisKey]
      if (value >= minValue && value < maxValue + limit) {
        return {
          ...item,
          'selectedKey': item[dataKey]
        }
      } else {
        return item
      }
    })

    const bandsWidth = (width - HANDLER_WIDTH * 2) / chartData.length
    const outerPadding = HANDLER_WIDTH / bandsWidth
    const innerPadding = 1 / bandsWidth

    return (
      <StackedBarChart
        className='range-slider_chart'
        width={width}
        height={36}
        data={processedData}
        max={maxBy(chartData, item => item[dataKey])[dataKey]}
        min={0}
        quantize={9}
        innerPadding={innerPadding}
        outerPadding={outerPadding}
        xAxisKey={xAxisKey}
        tooltip={tooltip}
      >
        <StackBar dataKey={dataKey} color='almost-white' />
        <StackBar dataKey='selectedKey' color='pale-brand' />
      </StackedBarChart>
    )
  }

  onClick = event => {
    const {chartData, disabled} = this.props
    const {isOpened} = this.state

    if (chartData || disabled) return null

    this.setState({
      isOpened: !isOpened
    })

    return event.nativeEvent.stopImmediatePropagation()
  }

  onClearClick = event => {
    const {onSelect} = this.props

    event.stopPropagation()
    onSelect && onSelect(null)
  }

  onPreventClick = event => {
    event.stopPropagation()
    return event.nativeEvent.stopImmediatePropagation()
  }

  onMoveHandling = event => {
    event.stopPropagation()
    const {disabled} = this.props
    const {handling, distance, startMouse} = this.state
    if (disabled || !handling) return null

    const diff = Math.floor((event.clientX - startMouse) / distance)

    if (handling === 'left') return this.processLeftHandling(diff)
    if (handling === 'right') return this.processRightHandling(diff)
  }

  onStartHandling = (type, event) => {
    const {disabled} = this.props

    event.stopPropagation()

    if (disabled) return null

    this.setState({
      handling: type,
      startMouse: event.clientX
    })
  }

  onStopHandling = event => {
    const {disabled} = this.props

    event.stopPropagation()

    if (disabled) return null

    const {onSelect} = this.props
    const {minValue: min, maxValue: max} = this.state
    this.setState({handling: null})

    onSelect && onSelect({min, max})
  }

  hideSlider = event => {
    this.setState({isOpened: false})
  }

  getMinValue(props) {
    const {min, values} = props || this.props
    const {min: minValue} = values || {}

    if (minValue === undefined || minValue === null) return min

    return minValue < min ? min : minValue
  }

  getMaxValue(props) {
    const {max, values} = props || this.props
    const {max: maxValue} = values || {}

    if (maxValue === undefined || maxValue === null) return max

    return maxValue > max ? max : maxValue
  }

  getLeftPosition({distance, minValue, min, step, stepValues}) {
    if (stepValues) {
      return distance * stepValues.indexOf(minValue)
    } else {
      return distance * ((minValue - min) / step)
    }
  }

  getRightPosition({distance, maxValue, min, step, stepValues}) {
    const {limit} = this.props

    if (stepValues) {
      return distance * stepValues.indexOf(maxValue) + HANDLER_WIDTH * 2
    } else {
      return distance * ((maxValue - min + limit) / step) + HANDLER_WIDTH * 2
    }
  }

  processLeftHandling(diff) {
    const {distance} = this.state
    const {min, step, stepValues} = this.props
    const minValue = this.getMinValue()
    const maxValue = this.getMaxValue()
    const value = stepValues ? stepValues[stepValues.indexOf(minValue) + diff] : minValue + diff * step

    if (value === undefined || value < min || value > maxValue) return null

    this.setState({
      leftPosition: this.getLeftPosition({distance, minValue: value, min, step, stepValues}),
      minValue: value
    })
  }

  processRightHandling(diff) {
    const {distance} = this.state
    const {min, max, step, stepValues} = this.props
    const minValue = this.getMinValue()
    const maxValue = this.getMaxValue()
    const value = stepValues ? stepValues[stepValues.indexOf(maxValue) + diff] : maxValue + diff * step

    if (value === undefined || value < minValue || value > max) return null

    this.setState({
      rightPosition: this.getRightPosition({distance, maxValue: value, min, step, stepValues}),
      maxValue: value
    })
  }

  calculateValues(props) {
    const {min, max, width, step, stepValues, limit} = props
    const absoluteValue = stepValues ? stepValues.length - 1 : (max - (min - limit)) / step
    const distance = (width - HANDLER_WIDTH * 2) / absoluteValue
    const minValue = this.getMinValue(props)
    const maxValue = this.getMaxValue(props)

    this.setState({
      distance,
      leftPosition: this.getLeftPosition({distance, minValue, min, step, stepValues}),
      minValue,
      rightPosition: this.getRightPosition({distance, maxValue, min, step, stepValues}),
      maxValue
    })
  }
}



// WEBPACK FOOTER //
// ./client/components/RangeSlider/index.js